import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import "./TaskEditor2.css";
import { AppContext } from '../App';
import { TaskType, Task, TaskTimeConstraint } from 'konect-model';
import { MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBBtn, MDBModalBody, MDBInput, MDBIcon, MDBModalFooter } from 'mdb-react-ui-kit';
import AutoCompleteInput from './AutoCompleteInput';
import DropdownSelect, { createDropdownItemArray } from './DropdownSelect';
import { nanoid } from 'nanoid';
import UserControlService from '../../services/user-control-service';
import { ModalState } from './TaskEditor2';

interface TaskEditorModalProps {
    modalState: ModalState;
    setModalState: (state: ModalState) => void;
}

interface DummyRule {
    id: string;
    ie: string;
    type: TaskType[];
}

interface TaskEditorCtx {
    handleAddButton: (task: Task) => void;
    handleEditButton: (task: Task) => void;
}
export const TaskEditorContext = createContext<TaskEditorCtx>({handleAddButton: () => {}, handleEditButton: () => {}});

export default function TaskEditorModal(props : TaskEditorModalProps) {

    const { project, setProject, editingTask, setEditingTask } = useContext(AppContext);
    const [interfaceElementList, setInterfaceElementList] = useState<string[]>([]);
    const [stateList, setStateList] = useState<string[]>([]);


    const [dummyRuleList, setDummyRuleList] = useState<DummyRule[]>([]);
    const dummyRuleListRef = useRef<DummyRule[]>([]);
    const [dummyConstraintList, setDummyConstraintList] = useState<TaskTimeConstraint[]>([]);
    const [dummyStateList, setDummyStateList] = useState<string[]>([]);
    const [taskName, setTaskName] = useState<string>("");
    const [dummyTask, setDummyTask] = useState<Task>(new Task());
    const [rulesOkay, setRulesOkay] = useState<boolean>(false);

    useEffect(() => {
        dummyRuleListRef.current = dummyRuleList;
    }, [dummyRuleList])

    // used to disable the save/apply buttons, if a rule is specified without a type (information/interaction)
    useEffect(() => {
        checkRules();
    }, [dummyRuleList])

    const checkRules = () => {
        const checkList = dummyRuleList.slice(0, -1);
        setRulesOkay(checkList.every((rule) => rule.type.length));
    }

    /* EDITOR FUNCTIONALITY */

    // Converts the DummyTask with its components to a new Task in the project, when the user clicks save on the "Add Task" dialogue
    const addCell = () => {
        UserControlService.update();
        dummyTask.name = taskName;
        dummyTask.id = nanoid();
        dummyRuleList.forEach((rule) => {
            Task.addTaskRule(project, dummyTask, rule.id, rule.ie, rule.type);
        });
        dummyTask.timeConstraint = [...dummyConstraintList.slice(0, -1)];
        dummyTask.states = [...dummyStateList.slice(0, -1)];
        dummyTask.parent = editingTask.id;
        editingTask.subtasks.push(dummyTask);
        closeMenus();
        setProject(project);
    }

    // Applies the changes made to the DummyTask to the EditingTask, when the user clicks save on the "Edit Task" dialogue
    const editCell = () => {
        UserControlService.update();

        // **Name**
        editingTask.name = taskName;
        // **Rules** 
        setDummyRuleList(dummyRuleList.slice(0, -1)); // remove the empty placeholder rule (it should always be empty)
        const dummyRuleListIds = new Set(dummyRuleList.map(rule => rule.id));
        // Execute removeTaskRule for items only in taskRules
        editingTask.rules.forEach(rule => {
            if (!dummyRuleListIds.has(rule.id)) Task.removeTaskRule(project, editingTask, rule.id);
        });
        const editingTaskRuleIds = new Set(editingTask.rules.map(rule => rule.id));
        dummyRuleList.forEach(rule => {
            // Execute updateTaskRule for items in both lists
            if (editingTaskRuleIds.has(rule.id)) Task.updateTaskRule(project, editingTask, rule.id, rule.ie, rule.type);
            // Execute addTaskRule for items only in dummyRuleList
            else Task.addTaskRule(project, editingTask, rule.id, rule.ie, rule.type);
        });
        // **Task Constraints**
        editingTask.timeConstraint = [...dummyConstraintList.slice(0, -1)];
        // **States/Variables**
        editingTask.states = [...dummyStateList.slice(0, -1)];

        closeMenus();
        setProject(project);
    }


    /* RULE EDITING */

    // Helper function to get all the interface elements, when editing a task
    const getInterfaceElements = () => {
        const interfaceElements: string[] = [];

        const readInterfaceElements = (task: Task) => {
            task.rules.forEach((rule) => {
                if (rule.lhs && rule.lhs.retrieves && rule.lhs.retrieves.length && !interfaceElements.includes(rule.lhs.retrieves[0].variable)) interfaceElements.push(rule.lhs.retrieves[0].variable);
                if (rule.rhs && rule.rhs.motor && rule.rhs.motor.length && !interfaceElements.includes(rule.rhs.motor[0].variable)) interfaceElements.push(rule.rhs.motor[0].variable);
            });
            if (task.subtasks && task.subtasks.length > 0) {
                for (const subtask of task.subtasks) readInterfaceElements(subtask);
            }
        }

        readInterfaceElements(project.taskModel.rootTask);
        return interfaceElements;
    }

    // Helper function to get to get all the rules, when editing a task
    const getRules = (task: Task) => {
        const ruleList: DummyRule[] = [];
        task.rules.forEach((rule) => {
            let ie = "";
            let type = [];
            if (rule.lhs && rule.lhs.retrieves && rule.lhs.retrieves.length) {
                ie = rule.lhs.retrieves[0].variable;
                type.push(TaskType.INFORMATION);
            }
            if (rule.rhs && rule.rhs.motor && rule.rhs.motor.length) {
                ie = rule.rhs.motor[0].variable;
                type.push(TaskType.INTERACTION);
            }
            if (type.length) ruleList.push({ id: rule.id, ie: ie, type: type })
        })
        ruleList.push({ id: nanoid(), ie: "", type: [] });
        return ruleList;
    }

    // Helper function to update a rule
    const updateRule = (id: string, value: DummyRule) => {
        var dummyRuleList = dummyRuleListRef.current;
        const newRuleList = dummyRuleList.map((rule) => {
            if (rule.id === id) {
                return value;
            } else return rule;
        });
        //adds a rule, if the last entry is not empty anymore
        const lastRule = newRuleList[newRuleList.length - 1];
        if (lastRule.ie || lastRule.type.length) setDummyRuleList([...newRuleList, { id: nanoid(), ie: "", type: [] }]);
        else setDummyRuleList(newRuleList);
    }

    // Adds/removes the interaction/information type from a certain rule (by index); is used in the rule type toggle buttons
    const handleTypeChange = (id: string, type: TaskType) => {
        const rule = dummyRuleList.find(rule => rule.id === id);
        if (!rule) return;
        if (type === TaskType.INTERACTION) {
            if (rule.type.includes(type)) rule.type = rule.type.filter((t) => t !== type);
            else rule.type.push(type);
        } else if (type === TaskType.INFORMATION) {
            if (rule.type.includes(type)) rule.type = rule.type.filter((t) => t !== type);
            else rule.type.push(type);
        }
        updateRule(rule.id, { id: rule.id, ie: rule.ie, type: rule.type });
    }

    // Removes a rule of the task based on the specified index 
    const deleteRule = (index: number) => {
        const newRuleList = dummyRuleList.filter((rule, i) => i !== index);
        setDummyRuleList(newRuleList);
    }

    
    /* CONSTRAINT EDITING */

    // Helper function to get all tasks for the task dropdown
    const getAllTasks = () : Task[] => {
        const taskList:Task[] = [];
        const rootTask = project.taskModel.rootTask;

        const addTaskToList = (task:Task) => {
            taskList.push(task);
            if (task.subtasks && task.subtasks.length > 0) task.subtasks.forEach(subtask => addTaskToList(subtask));
        }
        addTaskToList(rootTask);
        return taskList;
    }
    
    // Updates a constraint of a task based on the specified index
    const updateConstraint = (index: number, task: Task, naryOps: String) => {
        const newEntry = new TaskTimeConstraint();
        newEntry.task = task.id;
        newEntry.naryOps = naryOps as 'choice' | 'orderIndependence' | 'interleaving' | 'parallelism' | 'synchronisation' | 'disabling' | 'suspendResume' | 'enabling';
        dummyConstraintList[index] = newEntry;
        //adds a rule, if the last entry is not empty anymore
        const lastConstraint = dummyConstraintList[dummyConstraintList.length - 1];
        if (lastConstraint !== undefined && lastConstraint.task !== undefined) setDummyConstraintList([...dummyConstraintList, new TaskTimeConstraint()]);
        else setDummyConstraintList([...dummyConstraintList]);
    }

    // Removes a constraint of the task based on the specified index
    const deleteConstraint = (index: number) => {
        setDummyConstraintList(dummyConstraintList.filter((constraint, i) => i !== index));
    }


    /* STATE EDITING */

    // Helper function to get all the interface elements, when editing a task
    const getStates = () => {
        const states: string[] = [];

        const readStates = (task: Task) => {
            if (task.states) {
                for (const state of task.states) {
                    if (states.find(s => s === state)) continue;
                    states.push(state);
                }
            }
            if (task.subtasks && task.subtasks.length > 0) {
                for (const subtask of task.subtasks) readStates(subtask);
            }
        }

        readStates(project.taskModel.rootTask);
        return states;
    }

    // Updates a state of the task based on the specified index
    const updateState = (index: number, value: string) => {
        dummyStateList[index] = value;
        //adds a rule, if the last entry is not empty anymore
        const lastState = dummyStateList[dummyStateList.length - 1];
        if (lastState !== undefined && lastState !== "")
            setDummyStateList([...dummyStateList, ""]);
        else
            setDummyStateList([...dummyStateList]);
    }

    // Removes a state of the task based on the specified index
    const deleteState = (index: number) => {
        const newStateList = dummyStateList.filter((rule, i) => i !== index);
        setDummyStateList(newStateList);
    }

    /* MENU STUFF */

    useEffect(() => {
        if (props.modalState === ModalState.ADD) {
            setInterfaceElementList(getInterfaceElements());
            setStateList(getStates());
            setDummyRuleList([{ id: nanoid(), ie: "", type: [] }]);
            setDummyConstraintList([new TaskTimeConstraint()])
            setDummyStateList([""])
            setDummyTask(new Task());
    
            document.getElementById("taskname-input")?.focus(); // focus taskname input field after the add task modal pops up
        }
        if (props.modalState === ModalState.EDIT) {
            const task = editingTask;
            setInterfaceElementList(getInterfaceElements());
            setStateList(getStates());
            setTaskName(task.name);
            setDummyRuleList(getRules(task));
            setDummyConstraintList(task.timeConstraint && task.timeConstraint.length > 0 ? [...task.timeConstraint, new TaskTimeConstraint()] : [new TaskTimeConstraint()])
            setDummyStateList(task.states && task.states.length > 0 ? [...task.states, ""] : [""])
            setDummyTask({ ...task });
        }
    }, [props.modalState])

    // Hides the modal and resets the respective states
    const closeMenus = () => {
        setEditingTask(new Task());
        setDummyRuleList([]);
        setDummyConstraintList([]);
        props.setModalState(ModalState.HIDDEN);
        setTaskName("");
    }

    useEffect(() => {
        const handleEscape = (event:any) => {
            if (event.key === "Escape") closeMenus();
        };
        const handleEnter = (event:any) => {
            if (event.key === "Enter") {
                const activeElement = document.activeElement;
                if (activeElement && (activeElement.tagName === "INPUT" || activeElement.tagName === "TEXTAREA")) return;
                if (!taskName || !rulesOkay) return;
                event.preventDefault();
                if (props.modalState === ModalState.ADD) addCell();
                if (props.modalState === ModalState.EDIT) editCell();
            }
        };
    
        document.addEventListener("keydown", handleEscape);
        document.addEventListener("keydown", handleEnter);
        return () => {
            document.removeEventListener("keydown", handleEscape);
            document.removeEventListener("keydown", handleEnter);
        };
      }, [taskName, rulesOkay, dummyTask, project, dummyRuleList, editingTask]);

    return <MDBModal staticBackdrop show={true}>
        <MDBModalDialog size="lg">
            <MDBModalContent>
                <MDBModalHeader>
                    {props.modalState === ModalState.ADD && <MDBModalTitle>Add new subtask to '{editingTask.name}'</MDBModalTitle>}
                    {props.modalState === ModalState.EDIT && <MDBModalTitle>Edit task '{editingTask.name}'</MDBModalTitle>}
                    <MDBBtn className='btn-close' color='none' onClick={() => { closeMenus() }}></MDBBtn>
                </MDBModalHeader>
                <MDBModalBody style={{ maxHeight: "80vh", overflowY: "auto" }}>
                    <div className="d-flex flex-column mb-4 gap-2 align-items-start">
                        <h2>Task Name</h2>
                        <MDBInput id='taskname-input' label="Task Name" wrapperClass="w-50" type="text" value={taskName} onChange={(e: any) => setTaskName(e.target.value)} />
                    </div>
                    <hr />
                    <div className="d-flex flex-column gap-2">
                        <h2 className="align-self-start">Rules</h2>
                        {dummyRuleList.map((rule, index) => {
                            return (<div key={rule.id} className="d-flex flex-row gap-2 align-items-center justify-content-between pt-2">
                                {index + 1}
                                <AutoCompleteInput<string>
                                    valueList={interfaceElementList}
                                    value={rule.ie}
                                    setValue={(value: string) => { updateRule(rule.id, { id: rule.id, ie: value, type: rule.type }) }}
                                    label="Interface Element Name"
                                    style={{ width: "50%" }}
                                />
                                <div className="iButton-wrapper">
                                    <MDBBtn className={`iButton ${rule.type.includes(TaskType.INFORMATION) ? "informationButton" : "iButton"} d-flex gap-2 align-items-center`} onClick={() => { handleTypeChange(rule.id, TaskType.INFORMATION) }}>
                                        <MDBIcon fas icon="eye" style={{ fontSize: "20px" }} />
                                        Perception
                                    </MDBBtn>
                                    <MDBBtn className={`${rule.type.includes(TaskType.INTERACTION) ? "interactionButton" : "iButton"} d-flex gap-2 align-items-center`} onClick={() => { handleTypeChange(rule.id, TaskType.INTERACTION) }}>
                                        <MDBIcon fas icon="hand-paper" style={{ fontSize: "20px" }} />
                                        Interaction
                                    </MDBBtn>
                                </div>
                                <div className="editTaskDelete">
                                    <MDBBtn color="danger" style={index + 1 === dummyRuleList.length ? { visibility: "hidden" } : {}} onClick={() => { deleteRule(index) }}>
                                        <i className="fas fa-trash"></i>
                                    </MDBBtn>
                                </div>
                            </div>)
                        })}
                    </div>
                    <hr />
                    <div className="d-flex flex-column gap-2">
                        <h2 className="align-self-start">Task Constraints</h2>
                        {dummyConstraintList.map((tc, index) => {
                            return (<div key={index} className="d-flex flex-row gap-2 align-items-center justify-content-between pt-2">
                            {index + 1}
                            <AutoCompleteInput<Task>
                                valueList={getAllTasks().filter(task => task.id !== editingTask.id)}
                                value={Task.findTask(project.taskModel.rootTask, tc.task) || new Task()}
                                setValue={(task: Task) => updateConstraint(index, task, tc.naryOps)}
                                valueToString={(task:Task) => { return task ? task.name : "" }}
                                label="Task"
                                style={{ width: "100%" }}
                                allowInput={false}
                            />
                            <div style={{minWidth: "200px"}}>
                                <DropdownSelect
                                    type="constraint"
                                    value={tc.naryOps} 
                                    defaultValue={'choice'}
                                    items={createDropdownItemArray(['choice', 'orderIndependence',  'interleaving',  'parallelism',  'synchronisation',  'disabling',  'suspendResume',  'enabling'].sort())}
                                    onClick={(e) => updateConstraint(index, Task.findTask(project.taskModel.rootTask, tc.task) || new Task(), JSON.parse(e.target.dataset["mssg"]).name)}
                                />
                            </div>
                            <div className="editTaskDelete">
                                <MDBBtn color="danger" style={index + 1 === dummyConstraintList.length ? { visibility: "hidden" } : {}} onClick={() => deleteConstraint(index)}>
                                    <i className="fas fa-trash"></i>
                                </MDBBtn>
                            </div>
                        </div>)
                        })}
                    </div>
                    <hr />
                    <div className="d-flex flex-column gap-2">
                        <h2 className="align-self-start">States/Variables</h2>
                        <div className="d-flex flex-row gap-2 align-items-center justify-content-start pt-2">
                            0
                            <AutoCompleteInput<string>
                                valueList={[]}
                                value={"Default"}
                                setValue={(e: any) => { }}
                                label="State Name"
                                style={{ width: "fit-content" }}
                                disabled={true}
                            />
                        </div>
                        {dummyStateList.map((state, index) => {
                            return (<div key={index} className="d-flex flex-row gap-2 align-items-center justify-content-between pt-2">
                                {index + 1}
                                <AutoCompleteInput<string>
                                    valueList={stateList}
                                    value={state}
                                    setValue={(value: string) => { updateState(0, value) }}
                                    label="State Name"
                                    style={{ width: "100%" }}
                                />
                                <div className="editTaskDelete">
                                    <MDBBtn color="danger" style={index + 1 === dummyStateList.length ? { visibility: "hidden" } : {}} onClick={() => { deleteState(index) }}>
                                        <i className="fas fa-trash"></i>
                                    </MDBBtn>
                                </div>
                            </div>)
                        })}
                    </div>
                </MDBModalBody>
                <MDBModalFooter>
                    {props.modalState === ModalState.ADD && <MDBBtn disabled={!taskName || !rulesOkay} onClick={() => { addCell() }}>
                        Apply
                    </MDBBtn>}
                    {props.modalState === ModalState.EDIT && <MDBBtn disabled={!taskName || !rulesOkay} onClick={() => { editCell() }}>
                        Save
                    </MDBBtn>}
                    <MDBBtn onClick={() => { closeMenus() }}>
                        Cancel
                    </MDBBtn>
                </MDBModalFooter>
            </MDBModalContent>
        </MDBModalDialog>
    </MDBModal>
}